import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { exportToCsv } from "../../lib/exportCsv";
import AdminConfirmDialog from "./AdminConfirmDialog";
import BulkActionBar from "./BulkActionBar";
import { RefreshCw, Clock, Users } from "lucide-react";

const KINDS = [
    { id: "all", label: "All" },
    { id: "weekly", label: "Weekly" },
    { id: "monthly", label: "Monthly" },
];

export default function ChallengesTab() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [kind, setKind] = useState("all");
    const [activeOnly, setActiveOnly] = useState(true);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState([]);
    const [confirm, setConfirm] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/challenges", {
                params: { kind, active_only: activeOnly, page: 1, page_size: 100 },
            });
            setItems(data.items || []);
            setTotal(data.total || 0);
            setSelected([]);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [kind, activeOnly]);

    useEffect(() => { load(); }, [load]);

    const toggleAll = () =>
        setSelected(selected.length === items.length ? [] : items.map((i) => i.id));
    const toggleOne = (id) =>
        setSelected((sel) => (sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]));

    const reseed = async () => {
        try {
            await api.post("/admin/challenges/reseed");
            toast.success("Current week + month challenges ensured");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const runBulk = async (action, reason) => {
        try {
            const { data } = await api.post(`/admin/challenges/bulk?action=${action}`, {
                ids: selected,
                reason,
            });
            toast.success(`${data.affected} challenges ${action}ed`);
            setConfirm(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const endOne = async (id) => {
        try {
            await api.post(`/admin/challenges/${id}/end`);
            toast.success("Challenge ended");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const onExportCsv = () => {
        const rows = selected.length
            ? items.filter((i) => selected.includes(i.id))
            : items;
        exportToCsv(
            rows,
            [
                { key: "id", label: "ID" },
                { key: "title", label: "Title" },
                { key: "kind", label: "Kind" },
                { key: "period_key", label: "Period" },
                { key: "target_count", label: "Target" },
                { key: "participants", label: "Participants" },
                { key: "completed", label: "Completed" },
                { key: "starts_at", label: "Starts" },
                { key: "ends_at", label: "Ends" },
            ],
            "challenges"
        );
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex gap-1 bg-snowcap border border-ridge-line rounded-lg p-1">
                    {KINDS.map((k) => (
                        <button
                            key={k.id}
                            data-testid={`challenges-kind-${k.id}`}
                            onClick={() => setKind(k.id)}
                            className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-widest ${
                                kind === k.id ? "bg-midnight-pine text-white" : "text-slate-gray hover:text-granite"
                            }`}
                        >
                            {k.label}
                        </button>
                    ))}
                </div>
                <label className="flex items-center gap-2 text-xs font-bold text-slate-gray cursor-pointer">
                    <input
                        type="checkbox"
                        data-testid="challenges-active-only"
                        checked={activeOnly}
                        onChange={(e) => setActiveOnly(e.target.checked)}
                    />
                    Active only
                </label>
                <button
                    data-testid="challenges-reseed-btn"
                    onClick={reseed}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-ember-orange text-white text-xs font-bold hover:bg-ember-dark"
                >
                    <RefreshCw className="w-3.5 h-3.5" /> Re-seed this week & month
                </button>
            </div>

            {selected.length > 0 && (
                <BulkActionBar
                    totalVisible={items.length}
                    selectedIds={selected}
                    onToggleSelectAll={toggleAll}
                    onClear={() => setSelected([])}
                    onExportCsv={onExportCsv}
                    testidPrefix="challenges-bulk"
                    actions={[
                        { id: "end", label: "End now", onClick: () => setConfirm({ action: "end", title: `End ${selected.length} challenge(s) now?`, requireReason: false, description: "Marks ends_at to now — participants can't make further progress." }) },
                        { id: "delete", label: "Delete", danger: true, onClick: () => setConfirm({ action: "delete", danger: true, title: `Delete ${selected.length} challenge(s)?`, requireReason: true, description: "Irreversible. Also wipes user progress." }) },
                    ]}
                />
            )}

            <div className="bg-snowcap border border-ridge-line rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-alpine-mist">
                            <tr className="text-left text-[10px] font-bold uppercase tracking-widest text-slate-gray">
                                <th className="py-3 pl-4 w-10">
                                    <input
                                        type="checkbox"
                                        data-testid="challenges-select-all-cb"
                                        checked={items.length > 0 && selected.length === items.length}
                                        onChange={toggleAll}
                                    />
                                </th>
                                <th className="py-3 px-4">Title</th>
                                <th className="py-3 px-4">Kind</th>
                                <th className="py-3 px-4">Progress</th>
                                <th className="py-3 px-4">Window</th>
                                <th className="py-3 px-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={6} className="py-10 px-4 text-center text-slate-gray">Loading…</td></tr>
                            ) : items.length === 0 ? (
                                <tr><td colSpan={6} className="py-10 text-center text-slate-gray">No challenges match.</td></tr>
                            ) : (
                                items.map((c) => (
                                    <tr key={c.id} data-testid={`challenge-row-${c.id}`} className="border-t border-ridge-line">
                                        <td className="py-3 pl-4">
                                            <input
                                                type="checkbox"
                                                data-testid={`challenge-cb-${c.id}`}
                                                checked={selected.includes(c.id)}
                                                onChange={() => toggleOne(c.id)}
                                            />
                                        </td>
                                        <td className="py-3 px-4">
                                            <div className="font-semibold text-granite">{c.title}</div>
                                            <div className="text-xs text-slate-gray line-clamp-1">{c.description}</div>
                                        </td>
                                        <td className="py-3 px-4">
                                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-trail-tan/40 text-granite capitalize">
                                                {c.kind}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4 text-xs text-slate-gray">
                                            <div className="inline-flex items-center gap-1">
                                                <Users className="w-3 h-3" /> {c.participants || 0} · {c.completed || 0} done
                                            </div>
                                        </td>
                                        <td className="py-3 px-4 text-xs text-slate-gray">
                                            <div className="inline-flex items-center gap-1">
                                                <Clock className="w-3 h-3" />
                                                {c.period_key}
                                            </div>
                                        </td>
                                        <td className="py-3 px-4 text-right">
                                            <button
                                                data-testid={`challenge-end-${c.id}`}
                                                onClick={() => endOne(c.id)}
                                                className="px-2.5 py-1 rounded-md text-[10px] font-bold border border-ridge-line hover:border-ember-orange hover:text-ember-dark"
                                            >
                                                End now
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="p-4 border-t border-ridge-line text-xs text-slate-gray">
                    {total} total
                </div>
            </div>

            <AdminConfirmDialog
                open={!!confirm}
                onClose={() => setConfirm(null)}
                onConfirm={(reason) => runBulk(confirm.action, reason)}
                title={confirm?.title || ""}
                description={confirm?.description}
                confirmLabel="Apply"
                danger={confirm?.danger}
                requireReason={confirm?.requireReason}
            />
        </div>
    );
}
